import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { useMutation, useQuery } from "react-query"
import InvoiceSettled from "src/main/Account/PaymentPortal/InvoiceSettled"
import { paymentMethodIsCreditCard } from "src/main/Billing/helpers"

import AlertBanner from "src/components/AlertBanner"
import Button from "src/components/Button"
import HeadingPageTitle from "src/components/Heading/HeadingPageTitle"
import SelectPaymentMethod from "src/components/SelectPaymentMethod/SelectPaymentMethod"

import { getInvoice, payInvoice } from "src/api/Account/PaymentPortal"

import { useToast } from "src/hooks/use_toast"

const PayInvoice = ({
  combinedInvoice,
  convenienceFee,
  paymentMethods: initialPaymentMethods,
  acceptedPaymentMethods,
}) => {
  const {
    displayId,
    totalAmount,
    totalAmountDollars,
    settledStatus,
    detailsPath,
    marinaName,
    reservationId,
    parentRecordType,
    saleId,
    returnPath,
  } = combinedInvoice

  const convenienceFeeEligible = Object.keys(convenienceFee).length === 3
  const [expectedTotalAmountDollars, setExpectedTotalAmountDollars] =
    useState(totalAmountDollars)
  const [finalInvoiceTotalDollars, setFinalInvoiceTotalDollars] = useState(null)
  const [paymentMethods, setPaymentMethods] = useState(initialPaymentMethods)
  const [paymentMethod, setPaymentMethod] = useState("")
  const [invoiceSettled, setInvoiceSettled] = useState(
    settledStatus === "settled"
  )
  const [invoiceProcessing, setInvoiceProcessing] = useState(
    settledStatus === "processing"
  )
  const showToast = useToast()

  useEffect(() => {
    if (convenienceFeeEligible) {
      const isCreditCard = paymentMethodIsCreditCard(
        "stripePaymentMethodId",
        paymentMethod,
        paymentMethods
      )

      setExpectedTotalAmountDollars(
        isCreditCard
          ? convenienceFee.totalAmountWithFeeDollars
          : totalAmountDollars
      )
    }
  }, [
    convenienceFee,
    convenienceFeeEligible,
    paymentMethod,
    paymentMethods,
    totalAmount,
    totalAmountDollars,
  ])

  useQuery(
    ["invoiceStatus", displayId],
    () => getInvoice({ invoiceId: displayId }),
    {
      enabled: invoiceProcessing,
      refetchInterval: 3000,
      onSuccess: (data) => {
        if (!finalInvoiceTotalDollars) {
          // the first time we set finalInvoiceTotalDollars it should be a value
          // inclusive of a convenience fee if one was charged. Should payment
          // processing fail with Stripe, such that we remove the convenience fee,
          // we then begin getting back a value *without* a fee but do not want to
          // update finalInvoiceTotalDollars to that value so as not to confuse the
          // boater about how much their invoice is/how much we attempted to charge
          setFinalInvoiceTotalDollars(data.totalAmountDollars)
        }
        if (data.settledStatus === "settled") {
          setInvoiceProcessing(false)
          setInvoiceSettled(true)
        }
      },
    }
  )

  const { mutate: makePayment, isLoading: makingPayment } = useMutation(
    () =>
      payInvoice({
        invoiceId: displayId,
        paymentMethod,
        reservationId,
        saleId,
        amount: totalAmount,
      }),
    {
      onSuccess: (data) => {
        showToast(data.message, { type: "success" })
        setInvoiceProcessing(true)
      },
      onError: (error) => {
        showToast(error.message, { type: "error" })
        setInvoiceProcessing(false)
      },
    }
  )

  const renderInvoiceDetails = () => (
    <div className="card flex flex-col">
      <span className="font-semibold">{marinaName}</span>
      <span>
        {parentRecordType} #{saleId || reservationId}
      </span>
      <div className="mt-4 flex flex-col">
        <a href={detailsPath} className="font-semibold">
          View Invoice
        </a>
        <span className="text-gray-600">Reference: {displayId}</span>
      </div>
    </div>
  )

  const renderWithFeeText = () => {
    if (
      convenienceFeeEligible &&
      expectedTotalAmountDollars === convenienceFee.totalAmountWithFeeDollars
    ) {
      return ` ${convenienceFee.withFeeText}`
    }
  }

  const renderInvoiceAmount = () => (
    <div className="card flex flex-col space-y-4 font-semibold">
      <span className="text-base">Amount</span>
      <span className="text-lg">
        {expectedTotalAmountDollars}
        {renderWithFeeText()}
      </span>
    </div>
  )

  const renderPaymentMethodSection = () => (
    <div className="card">
      <SelectPaymentMethod
        acceptedPaymentMethods={acceptedPaymentMethods}
        paymentMethods={paymentMethods}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        setPaymentMethods={setPaymentMethods}
        trackingProps={{
          paymentMethodSelected: {
            eventName: "payment_portal:payment_method_selected",
            eventProps: { marina_name: marinaName },
          },
          paymentMethodAdded: {
            eventName: "payment_portal:payment_method_added",
            eventProps: { marina_name: marinaName },
          },
        }}
      />
    </div>
  )

  const renderConvenienceFeeNotice = () => {
    if (convenienceFeeEligible) {
      return <AlertBanner type="info">{convenienceFee.notice}</AlertBanner>
    }
  }

  const renderButtons = () => (
    <div className="flex flex-col space-y-2">
      <Button
        variant="primary"
        disabled={!paymentMethod || paymentMethod === "add"}
        onClick={makePayment}
        isLoading={makingPayment}
      >
        {makingPayment ? "Paying" : "Pay Now"}
      </Button>
      <a href={returnPath} className="btn btn-secondary">
        Cancel
      </a>
    </div>
  )

  if (invoiceSettled || (invoiceProcessing && finalInvoiceTotalDollars)) {
    return (
      <InvoiceSettled
        combinedInvoice={combinedInvoice}
        paymentMethods={paymentMethods}
        paymentMethod={paymentMethod}
        invoiceProcessing={invoiceProcessing}
        finalInvoiceTotalDollars={finalInvoiceTotalDollars}
      />
    )
  } else {
    return (
      <div className="flex flex-col space-y-4 py-10">
        <HeadingPageTitle>Make a payment</HeadingPageTitle>
        {renderInvoiceDetails()}
        {renderInvoiceAmount()}
        {renderPaymentMethodSection()}
        {renderConvenienceFeeNotice()}
        {renderButtons()}
      </div>
    )
  }
}

PayInvoice.propTypes = {
  combinedInvoice: PropTypes.shape({
    displayId: PropTypes.string.isRequired,
    totalAmount: PropTypes.string.isRequired,
    totalAmountDollars: PropTypes.string.isRequired,
    settledStatus: PropTypes.string.isRequired,
    detailsPath: PropTypes.string.isRequired,
    marinaName: PropTypes.string.isRequired,
    reservationId: PropTypes.string,
    parentRecordType: PropTypes.string.isRequired,
    saleId: PropTypes.string,
    returnPath: PropTypes.string.isRequired,
  }).isRequired,
  convenienceFee: PropTypes.shape({
    notice: PropTypes.string,
    totalAmountWithFeeDollars: PropTypes.string,
    withFeeText: PropTypes.string,
  }).isRequired,
  paymentMethods: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      stripePaymentMethodId: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      subtitle: PropTypes.string.isRequired,
    }).isRequired
  ),
  acceptedPaymentMethods: PropTypes.arrayOf(
    PropTypes.oneOf(["card", "us_bank_account"]).isRequired
  ).isRequired,
}

export default PayInvoice
